import React, { useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { Checkbox } from '@yme/atoms/checkbox';
import { ModalWindowFooter } from '@yme/atoms/modal-footer';
import { MD_MODAL, StaticModalWindow } from '@yme/atoms/modal-window';
import { Switcher } from '@yme/atoms/switcher';
import { getNewsletterInterests } from '@yme/components/Footer/Newsletters/redux/selectors';
import { subscribeNewsletter } from '@yme/components/Footer/Newsletters/redux/services';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { setIsModalHide } from '@yme/modals/actions';
import { getRTL } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { SWITCHER_OPTIONS, SWITCHER_NAME } from './constants';
import { getInitialOptions, getCheckedOptions } from './helpers';
import { SubtitleStyled, OptionsWrapperStyled } from './styled';
export const NewsletterModal = () => {
    const isRightToLeft = useSelector(getRTL);
    const { ymeInterests, racingInterests } = useSelector(getNewsletterInterests);
    const [currentValue, setCurrentValue] = useState(SWITCHER_OPTIONS.YME);
    const [ymeOptions, setYmeOptions] = useState(getInitialOptions(ymeInterests));
    const [racingOptions, setRacingOptions] = useState(getInitialOptions(racingInterests));
    const [isBottomSheetOpened, setIsBottomSheetOpened] = useState(true);
    const dispatch = useDispatch();
    const { isInfiniteDesktopView } = useWindowSize();
    const { t } = useTranslation();
    const handleHideModal = useCallback(() => {
        if (isInfiniteDesktopView) {
            dispatch(setIsModalHide());
        }
        else {
            setIsBottomSheetOpened(false);
        }
    }, [dispatch, isInfiniteDesktopView]);
    const handleChange = useCallback(({ target: { id, checked } }) => {
        const setOptions = currentValue === SWITCHER_OPTIONS.YME ? setYmeOptions : setRacingOptions;
        setOptions((prev) => prev.map((option) => {
            if (option.id === id) {
                return {
                    ...option,
                    checked,
                };
            }
            return option;
        }));
    }, [currentValue]);
    const handleCheckedCurrentValue = useCallback((value) => {
        setCurrentValue(value);
    }, []);
    const handleSubmit = useCallback(() => {
        dispatch(subscribeNewsletter(getCheckedOptions(ymeOptions), getCheckedOptions(racingOptions), t));
        handleHideModal();
    }, [dispatch, ymeOptions, racingOptions, t, handleHideModal]);
    const handleBottomSheetSpringEnd = useCallback(({ type }) => {
        if (type === 'CLOSE') {
            dispatch(setIsModalHide());
        }
    }, [dispatch]);
    const switcherProps = [
        {
            id: SWITCHER_OPTIONS.YME,
            label: t('newsletter_yamaha_tab'),
            value: SWITCHER_OPTIONS.YME,
            isChecked: true,
            isDisabled: false,
        },
        {
            id: SWITCHER_OPTIONS.RACING,
            label: t('newsletter_racing_tab'),
            value: SWITCHER_OPTIONS.RACING,
            isChecked: false,
            isDisabled: false,
        },
    ];
    const subscribeOptions = currentValue === SWITCHER_OPTIONS.YME ? ymeOptions : racingOptions;
    const isSubmitButtonDisabled = useMemo(() => ymeOptions.every((option) => !option.checked) &&
        racingOptions.every((option) => !option.checked), [racingOptions, ymeOptions]);
    const modalContent = (React.createElement(React.Fragment, null,
        React.createElement(Switcher, { checkedCurrentValue: handleCheckedCurrentValue, switcherArguments: switcherProps, isRightToLeft: isRightToLeft, name: SWITCHER_NAME }),
        React.createElement(SubtitleStyled, null, t('newsletter_whichNewsletterWouldYouLikeToReceive_message')),
        React.createElement(OptionsWrapperStyled, { isRightToLeft: isRightToLeft, "data-body-scroll-lock-ignore": true }, subscribeOptions.map(({ id, label, checked }) => (React.createElement(Checkbox, { key: id, id: id, isRightToLeft: isRightToLeft, checked: checked, value: label, onChange: handleChange }))))));
    const modalFooter = (React.createElement(ModalWindowFooter, { isSubmitButton: true, isCancelButton: true, cancelButtonLabel: t('common_cancel_button'), submitButtonLabel: t('newsletter_subscribe_button'), isSubmitButtonDisabled: isSubmitButtonDisabled, isSubmitButtonFullWidth: !isInfiniteDesktopView, isHiddenBorder: !isInfiniteDesktopView, onSubmit: handleSubmit, onCancel: handleHideModal }));
    if (isInfiniteDesktopView) {
        return (React.createElement(StaticModalWindow, { onHide: handleHideModal, minHeight: "auto", staticModalSize: MD_MODAL, title: t('newsletter_newsletterSubscription_header'), isRightToLeft: isRightToLeft, footerComponent: modalFooter }, modalContent));
    }
    return (React.createElement(BottomSheet, { initialFocusRef: false, open: isBottomSheetOpened, header: React.createElement(Header, { isRightToLeft: isRightToLeft, headerLeft: t('newsletter_newsletterSubscription_header') }), footer: modalFooter, onDismiss: handleHideModal, onSpringEnd: handleBottomSheetSpringEnd },
        React.createElement(FlexWrapper, { flexDirection: "column", padding: "16px" }, modalContent)));
};
